import { createGlobalStyle } from "styled-components";
import reset from "reset-css";

// Font awesome icons at:
// https://react-icons.github.io/react-icons/icons?name=fa

const size = {
  mobileS: "320px",
  mobileM: "375px",
  mobileL: "425px",
  tablet: "768px",
  laptop: "1024px",
  laptopM: "1200px",
  laptopL: "1440px",
  desktop: "2560px",
};

export const device = {
  mobileS: `(min-width: ${size.mobileS})`,
  mobileM: `(min-width: ${size.mobileM})`,
  mobileL: `(min-width: ${size.mobileL})`,
  tablet: `(min-width: ${size.tablet})`,
  laptop: `(min-width: ${size.laptop})`,
  laptopM: `(min-width: ${size.laptopM})`,
  laptopL: `(min-width: ${size.laptopL})`,
  desktop: `(min-width: ${size.desktop})`,
  desktopL: `(min-width: ${size.desktop})`,
};

export default createGlobalStyle`
  ${reset}

html {
    /* Brand — Confetti & Coral */
    --color-coral: #FF6B6B;
    --color-coral-deep: #E8484F;
    --color-coral-soft: #FFA89A;
    --color-plum: #3D1E5C;
    --color-plum-light: #6B3FA0;
    --color-gold: #F4C95D;
    --color-cream: #FFF8F0;
    --color-pink-soft: #FFE4E1;
    --color-pink-blush: #FCEDEA;

    /* Legacy aliases — kept so existing components keep working */
    --color-accent-prim: #FF6B6B;
    --color-accent-sec: #F4C95D;
    --color-accent-purple: #3D1E5C;
    --color-accent-orange: #FF6B6B;
    --color-accent-pink: #FCEDEA;
    --color-accent-orange-light: #FFA89A;

    /* Surfaces */
    --color-background-prim: #FFF8F0;
    --color-background-white: #ffffff;
    --color-background-grey: #FFF8F0;
    --color-surface: #ffffff;

    /* Text */
    --color-text: #1B0D2E;
    --color-text-light: #ffffff;
    --color-text-medium: #3D1E5C;
    --color-text-soft: #6B5B7A;

    /* Neutrals */
    --color-white: #ffffff;
    --color-black: #000000;
    --color-light-grey: #F6F1EC;
    --color-medium-grey: #E5DCD3;
    --color-dark-grey: #9C8E9F;

    /* Status */
    --color-status-new: #3A3A3A;
    --color-status-good: #1bb775;
    --color-status-info: #2986cc;
    --color-status-bad: #d62809;
    --color-status-warning: #d98a02;

    /* Radii */
    --radius-sm: 12px;
    --radius-md: 18px;
    --radius-lg: 28px;
    --radius-xl: 40px;

    /* Shadows — plum-tinted neutral, coral-tinted warm */
    --shadow-soft: 0 6px 18px rgba(61, 30, 92, 0.08);
    --shadow-medium: 0 14px 40px rgba(61, 30, 92, 0.12);
    --shadow-lift: 0 22px 60px rgba(232, 72, 79, 0.18);

    /* Gradients */
    --gradient-festive: linear-gradient(135deg, #FFD3C4 0%, #FFA89A 35%, #FF6B6B 70%, #E8484F 100%);
    --gradient-soft: linear-gradient(180deg, #FFF8F0 0%, #FCEDEA 100%);
    --gradient-coral-h: linear-gradient(90deg, #FF8A75 0%, #FF6B6B 55%, #E8484F 100%);

    font-size: 62.5%;
}

:root {
    position: relative;
    min-height: 100vh;
    width: 100vw;
    margin: auto;
    font-size: 62.5%;
    color: var(--color-text);
    font-family: 'Inter', 'Montserrat', sans-serif;
}

html, *{ box-sizing: border-box; }
*, *:before, *:after { box-sizing: inherit; }

::-webkit-scrollbar {
  -webkit-appearance: none;
  width: 8px;
  height: 8px;
}
::-webkit-scrollbar-thumb {
  border-radius: 8px;
  background-color: var(--color-coral-soft);
  box-shadow: 0 0 1px rgba(255, 255, 255, .5);
}
::-webkit-scrollbar-thumb:hover {
  background-color: var(--color-coral);
}

::selection {
  background: var(--color-coral-soft);
  color: var(--color-plum);
}

body {
  background: var(--gradient-soft) fixed;
  background-attachment: fixed;
  color: var(--color-text);
  font-family: 'Inter', 'Montserrat', sans-serif;
  text-align: left;
  font-size: 1.4rem;
  overflow-x: hidden;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}

/*-------main_style-------*/
p {
  line-height: 2.2rem;
  color: var(--color-text-soft);
}

a {
  text-decoration: none;
  display: inline-block;
  font-family: 'Inter', sans-serif;
  font-size: 1.6rem;
  color: var(--color-text);
  transition: color .2s ease, opacity .2s ease;
  &:hover{
    color: var(--color-coral);
    opacity: 1;
  }
}

b { font-weight: 700; }

i {
  font-style: italic;
  color: var(--color-coral);
}

button {
  text-decoration: none;
  text-align: center;
  text-transform: none;
  border-radius: 999px;
  font-size: 1.4rem;
  font-family: 'Inter', sans-serif;
  font-weight: 600;
  border: 1px solid transparent;
  background-color: var(--color-coral);
  color: var(--color-text-light);
  cursor: pointer;
  outline: none;
  padding: 0.9rem 1.8rem;
  letter-spacing: 0.01em;
  box-shadow: 0 6px 18px rgba(232, 72, 79, 0.20);
  transition: transform .2s ease, box-shadow .2s ease, background-color .2s ease, color .2s ease, border-color .2s ease;
  &:hover {
    background-color: var(--color-coral-deep);
    transform: translateY(-1px);
    box-shadow: 0 10px 26px rgba(232, 72, 79, 0.28);
  }
  &:active { transform: translateY(0); }
}

table {
  width: 100%;
  color: var(--color-text);
  font-family: 'Inter', sans-serif;
  border-collapse: collapse;
  th {
    border-bottom: 1px solid rgba(61, 30, 92, 0.12);
    color: var(--color-plum);
    font-weight: 600;
    font-size: 1.3rem;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    text-align: left;
    padding: 1.2rem 1rem 1.2rem 0;
  }
  tr {
    vertical-align: middle;
    transition: background-color .2s ease;
    &:hover td { background-color: rgba(255, 107, 107, 0.04); }
    &:last-of-type td { border-bottom: none; }
  }
  td {
    color: var(--color-text);
    padding: 1.2rem 1rem 1.2rem 0;
    vertical-align: middle;
    border-bottom: 1px solid rgba(61, 30, 92, 0.06);
  }
  &.table td { border-bottom: 1px solid rgba(61, 30, 92, 0.06); }
}

input[type="file"] { cursor: pointer; }

svg { cursor: pointer; }

/*-------Headers-------*/
h1, h2, h3, h4, h5, h6 {
  color: var(--color-plum);
  letter-spacing: -0.01em;
  font-family: 'Fraunces', 'Barlow Condensed', serif;
}

h1 {
  font-weight: 700;
  font-size: clamp(2.4rem, 5vw, 3.6rem);
  line-height: 1.05;
  text-transform: none;
  i, em { color: var(--color-coral); font-style: italic; }
}

h2 {
  font-weight: 600;
  font-size: clamp(2rem, 3vw, 2.6rem);
  line-height: 1.2;
  a { font-size: inherit; color: var(--color-plum); &:hover { color: var(--color-coral); } }
}

h3 {
  font-weight: 600;
  font-size: 1.8rem;
  line-height: 1.3;
}

h4 {
  font-weight: 500;
  font-size: 1.5rem;
  line-height: 1.35;
}

h5, h6 {
  font-weight: 500;
  font-size: 1.4rem;
  line-height: 1.35;
}

/*-------Form_elements-------*/
input, textarea {
  border: none;
  background-image: none;
  background-color: transparent;
  -webkit-box-shadow: none;
  -moz-box-shadow: none;
  box-shadow: none;
  font-family: 'Inter', sans-serif;
  font-size: 1.5rem;
  color: var(--color-text);
  &::placeholder { color: var(--color-dark-grey); }
  &:focus { outline: none; }
}

input {
  padding: 0.6rem 0.2rem;
  color: var(--color-text);
  border-bottom: 1.5px solid rgba(61, 30, 92, 0.16);
  font-size: 1.6rem;
  transition: border-color .2s ease, box-shadow .2s ease;
}

input:focus {
  outline: none !important;
  border-bottom: 1.5px solid var(--color-coral);
  box-shadow: 0 6px 14px -10px rgba(232, 72, 79, 0.45);
}

input[type='checkbox'] {
  accent-color: var(--color-coral);
}

textarea {
  border-radius: var(--radius-sm);
  background-color: rgba(61, 30, 92, 0.04);
  border: 1px solid transparent;
  padding: 1rem 1.1rem;
  transition: border-color .2s ease, box-shadow .2s ease, background-color .2s ease;
}

textarea:focus {
  outline: none !important;
  border: 1px solid var(--color-coral);
  background-color: var(--color-background-white);
  box-shadow: 0 6px 18px -10px rgba(232, 72, 79, 0.35);
}

select {
  width: 100%;
  padding: 0.9rem 1rem;
  border-radius: var(--radius-sm);
  background-color: rgba(61, 30, 92, 0.04);
  border: 1px solid transparent;
  font-size: 1.5rem;
  font-family: 'Inter', sans-serif;
  color: var(--color-text);
  transition: border-color .2s ease, background-color .2s ease;
  &:focus { outline: none; border-color: var(--color-coral); background-color: var(--color-background-white); }
}

option {
  font-size: 1.5rem;
  background-color: #ffffff;
}

.required:after {
  content: " *";
  color: var(--color-coral);
}

/*-------Other-------*/
.label {
  color: var(--color-text-soft);
  font-family: 'Inter', sans-serif;
  font-size: 1.2rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

label {
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 600;
  font-size: 1.3rem;
  color: var(--color-plum);
  letter-spacing: 0.01em;
}

/*-------Cards-------*/
.card {
  border-radius: var(--radius-lg);
  background-color: var(--color-background-white);
  color: var(--color-text);
  height: 100%;
  display: flex;
  flex-direction: column;
  border: 1px solid rgba(61, 30, 92, 0.05);
  box-shadow: var(--shadow-soft);
  overflow: hidden;
  transition: transform .25s ease, box-shadow .25s ease;
  &:hover {
    opacity: 1;
    transform: translateY(-4px);
    box-shadow: var(--shadow-medium);
  }
  a:hover { opacity: 1; color: var(--color-plum); }
  &__title {
    font-family: 'Fraunces', serif;
    font-weight: 600;
    font-size: 1.8rem;
    color: var(--color-plum);
    letter-spacing: -0.01em;
  }
  &__subtitle {
    color: var(--color-text-soft);
    font-family: 'Inter', sans-serif;
    font-weight: 500;
    font-size: 1.2rem;
    display: flex;
    svg { margin-left: .2rem; }
    path { fill: var(--color-text-soft); }
  }
}
.card_image__container {
  position: relative;
  width: 100%;
  padding-top: 52.6%;
  overflow: hidden;
  border-radius: var(--radius-lg) var(--radius-lg) 0 0;
  img {
    width: 100%;
    height: 100%;
    position: absolute;
    top: 0;
    left: 0;
    cursor: auto;
    object-fit: cover;
  }
}

.edit-button {
  position: absolute;
  top: 0.8rem;
  left: 0.8rem;
  cursor: pointer;
  z-index: 1;
}
.delete-button {
  position: absolute;
  top: 0.8rem;
  right: 0.8rem;
  cursor: pointer;
  z-index: 1;
}

.card__content {
  padding: 1.6rem 1.4rem;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  border-top: 1px solid rgba(61, 30, 92, 0.06);
  &__header {
    display: flex;
    justify-content: space-between;
    align-items: center;
  }
  &__body {
    margin: 1rem 0 1.2rem;
    font-weight: 400;
    color: var(--color-text-soft);
    line-height: 1.55;
  }
  &__footer {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    font-size: 1.4rem;
    font-family: 'Inter', sans-serif;
  }
}

.switch-container { display: flex; }
.switch-item {
  margin: 1.4rem;
  &-column { flex-direction: column; }
}

.modal-buttons {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  button { margin-right: 1rem; }
}

.cover-image {
  height: 20rem;
  width: 37rem;
  position: relative;
  border-radius: var(--radius-md);
  box-shadow: var(--shadow-soft);
}

.logo-image {
  height: 20rem;
  width: 20rem;
  position: relative;
  border-radius: var(--radius-md);
  box-shadow: var(--shadow-soft);
}

.page__header {
  & h1 { font-family: 'Fraunces', serif; }
}

.page__header__status {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.page__header__sub {
  display: flex;
  align-items: center;
  > * { margin-left: 1rem; }
}

.grid-add-btn {
  display: flex;
  align-items: center;
  margin: .8rem 0;
}

.thumbnail { padding: 6rem; }

.react-selectcomponent { color: var(--color-text); }

.form {
  input { width: 100%; }
  textarea {
    width: 100%;
    height: 10rem;
  }
  .form_row {
    margin-bottom: 2rem;
    display: flex;
    width: 100%;
    .field {
      width: 100%;
      &:not(:first-of-type) { margin-left: 2.4rem; }
    }
  }
  .error-message {
    opacity: 0.85;
    color: var(--color-status-bad);
    padding: 0.6rem 0;
    font-size: 1.3rem;
    font-weight: 500;
  }
  .buttons {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    button { margin-right: 1rem; }
  }
}

.icon-button {
  cursor: pointer;
  display: flex;
  align-items: center;
  color: var(--color-coral);
  font-size: 1.6rem;
  font-weight: 600;
  transition: color .2s ease, transform .2s ease;
  label {
    cursor: pointer;
    margin-left: .5rem;
    color: var(--color-text);
    font-weight: 500;
  }
  &:hover {
    transform: translateY(-1px);
    & label { color: var(--color-coral); }
    & svg { path { opacity: .8; } }
  }
}

.navigation-banner {
  display: grid;
  grid-template-columns: auto auto;
  align-items: baseline;
}

.back {
  transition: color .2s ease, transform .2s ease;
  &:hover {
    opacity: 1;
    color: var(--color-coral);
    transform: translateX(-2px);
    & svg { path { fill: var(--color-coral); } }
  }
}

.breadcrumbs {
  margin-bottom: 2rem;
  display: flex;
  justify-content: end;
  font-size: 1.3rem;
  color: var(--color-text-soft);
  li {
    display: inline-block;
    &:not(:last-child):after {
      content: " ›";
      color: var(--color-coral-soft);
      margin: 0 .2rem;
    }
    margin-right: .4rem;
  }
  .selected {
    opacity: 1;
    font-style: normal;
    color: var(--color-coral);
    font-weight: 600;
  }
}

.searchBarContainer {
  margin: 2rem 0;
  overflow: auto;
  background: var(--color-background-white);
  padding: 1rem 1.2rem;
  border-radius: var(--radius-md);
  border: 1px solid rgba(61, 30, 92, 0.05);
  box-shadow: var(--shadow-soft);
  table {
    width: 100%;
    min-width: 100rem;
  }
}

.switch-group {
  display: flex;
  border-bottom: 1px solid rgba(61, 30, 92, 0.10);
  margin-bottom: 1.2rem;
}

.switch-item p {
  padding-bottom: 0.4rem;
  font-weight: 500;
  font-size: 1.4rem;
  min-height: 3.3rem;
  color: var(--color-text-soft);
}

.switch-group-full-page {
  display: flex;
  margin-bottom: 1.2rem;
}

.switch-item-full-page {
  width: 100%;
  p {
    padding-bottom: 0.4rem;
    font-weight: 500;
    font-size: 1.4rem;
    min-height: 3.3rem;
    color: var(--color-text-soft);
  }
}

.description {
  padding: 1rem 0;
  color: var(--color-text-soft);
  line-height: 1.65;
  a {
    font-size: inherit;
    font-style: italic;
    color: var(--color-coral);
    &:hover { color: var(--color-coral-deep); }
  }
}

.filters .row {
  margin: 1rem 1rem 2rem 0rem;
  align-items: center;
  & > div {
    margin-right: 1rem;
    margin-bottom: 1rem;
  }
}

.clickable {
  cursor: pointer;
  border-radius: var(--radius-sm);
  transition: background-color .2s ease, color .2s ease;
  &:hover {
    background-color: rgba(255, 107, 107, 0.06);
    color: var(--color-coral);
  }
}

.select {
  background-color: rgba(255, 107, 107, 0.08);
  color: var(--color-coral-deep);
  font-style: normal;
  font-weight: 600;
  border-radius: var(--radius-sm);
}

/*-------Navigation-------*/
.nav {
  background: linear-gradient(180deg, #FFF8F0 0%, #FCEDEA 100%);
  height: 100%;
  box-shadow: 6px 0 24px -10px rgba(61, 30, 92, 0.18);
  padding-top: 2rem;
  border-right: 1px solid rgba(61, 30, 92, 0.05);
  & ul {
    display: flex;
    flex-direction: column;
    text-align: left;
    padding: 1rem 1rem 4rem;
    gap: 0.2rem;
  }
  & li { list-style: none; }
  & a {
    position: relative;
    font-size: 1.4rem;
    font-family: 'Inter', sans-serif;
    font-weight: 500;
    text-decoration: none;
    color: var(--color-plum);
    width: 100%;
    padding: 1rem 1.4rem 1rem 2rem;
    margin-top: 0.2rem;
    display: flex;
    align-items: center;
    text-transform: none;
    letter-spacing: 0.01em;
    border-radius: 999px;
    transition: background-color .2s ease, color .2s ease, transform .2s ease;
    & svg {
      margin-right: 0.9rem;
      width: 1.6rem;
      height: 1.6rem;
      color: var(--color-plum-light);
      transition: color .2s ease, transform .2s ease;
    }
    &:hover {
      color: var(--color-coral-deep);
      background-color: rgba(255, 107, 107, 0.08);
      opacity: 1;
      transform: translateX(2px);
      & svg {
        color: var(--color-coral);
        path { fill: var(--color-coral); }
      }
    }
    &.active {
      color: var(--color-coral-deep);
      background-color: rgba(255, 107, 107, 0.12);
      font-weight: 600;
      & svg {
        color: var(--color-coral-deep);
        path { fill: var(--color-coral-deep); }
      }
      &::after {
        content: "";
        position: absolute;
        left: 1rem;
        bottom: 0.3rem;
        width: 24px;
        height: 3px;
        border-radius: 999px;
        background: var(--color-coral);
        box-shadow: 0 2px 6px rgba(232, 72, 79, 0.4);
      }
    }
  }
  .reviewed_message {
    margin: 0 1.4rem 1rem;
  }
}

.buttons {
  margin-top: 1rem;
  display: flex;
  align-items: center;
  button { margin-right: 1rem; }
}

/*-------Design-system utility classes-------*/
.customButton {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
  padding: 0.9rem 1.8rem;
  min-width: 120px;
  border-radius: 999px;
  background: var(--color-coral);
  color: #fff;
  font: 600 1.4rem 'Inter', sans-serif;
  letter-spacing: 0.01em;
  border: 1px solid transparent;
  text-transform: none;
  cursor: pointer;
  box-shadow: 0 6px 18px rgba(232, 72, 79, 0.25);
  transition: background-color .2s ease, transform .2s ease, box-shadow .2s ease;
  &:hover {
    background: var(--color-coral-deep);
    transform: translateY(-1px);
    box-shadow: 0 10px 26px rgba(232, 72, 79, 0.32);
  }
}

.customButton2 {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
  padding: 0.85rem 1.7rem;
  border-radius: 999px;
  background: var(--color-background-white);
  color: var(--color-plum);
  font: 500 1.4rem 'Inter', sans-serif;
  border: 1.5px solid rgba(61, 30, 92, 0.12);
  text-transform: none;
  cursor: pointer;
  transition: color .2s ease, border-color .2s ease, transform .2s ease;
  &:hover {
    color: var(--color-coral);
    border-color: var(--color-coral);
    transform: translateY(-1px);
    background: var(--color-background-white);
  }
}

.customButton3 {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.4rem;
  padding: 0.85rem 1.7rem;
  border-radius: 999px;
  background: transparent;
  color: #fff;
  font: 500 1.4rem 'Inter', sans-serif;
  border: 1.5px solid rgba(255, 255, 255, 0.7);
  text-transform: none;
  cursor: pointer;
  transition: background-color .2s ease, transform .2s ease;
  &:hover {
    background: rgba(255, 255, 255, 0.12);
    transform: translateY(-1px);
  }
}

.heroBadge {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.4rem 0.95rem;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.7);
  border: 1px solid rgba(255, 107, 107, 0.25);
  color: var(--color-coral-deep);
  font: 600 1.1rem 'Inter', sans-serif;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  box-shadow: 0 4px 14px rgba(232, 72, 79, 0.08);
}

.categoryChip {
  display: inline-flex;
  align-items: center;
  padding: 0.25rem 0.7rem;
  border-radius: 999px;
  background: var(--color-pink-blush);
  color: var(--color-coral-deep);
  font: 600 1.05rem 'Inter', sans-serif;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.glassCard {
  background-color: rgba(255, 255, 255, 0.72);
  backdrop-filter: blur(12px) saturate(140%);
  -webkit-backdrop-filter: blur(12px) saturate(140%);
  border: 1px solid rgba(255, 255, 255, 0.55);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-soft);
}
`;
